import type { Rail, Vec2 } from "../level";
import { checkColor, rad, withColor, type PieceKind, type Placed, type PrimKind } from "./types";

/** A quarter pipe: a circular transition of radius `r` rising along u, then a flat deck `deck` long. */
export interface QuarterPrim extends Placed {
  kind: "quarter";
  r: number;
  deck: number;
  /** Render only (#240): its own color, and no steel coping drawn on its lip (a bank with `coping: false`). */
  color?: string;
  bare?: true;
}

/** A quarter pipe with a deck behind; coping on the lip unless `coping: false`. */
export interface QuarterPiece {
  kind: "quarter";
  x: number;
  z: number;
  rot?: number;
  r: number;
  hw: number;
  deck: number;
  coping?: boolean;
  /** Its own color (#240), over the map's `look.pieces`. */
  color?: string;
}

export const QUARTER: PrimKind<QuarterPrim> = {
  eval(p, u, w) {
    if (Math.abs(w) > p.hw || u < 0 || u > p.r + p.deck) return null;
    if (u >= p.r) return { h: p.r, du: 0, dw: 0 };
    const uu = Math.min(u, p.r - 1e-3);
    const root = Math.sqrt(p.r * p.r - uu * uu);
    return { h: p.r - root, du: uu / root, dw: 0, quarter: { u, r: p.r } };
  },
  extent: (p) => [0, p.r + p.deck],
  shadow: 0.55,
  top: (p) => p.r,
  plan: (p) => [{ u0: 0, u1: p.r, fill: "ramp" }, ...(p.deck > 0 ? [{ u0: p.r, u1: p.r + p.deck, fill: "deck" as const }] : [])],
};

/** The grindable lip of a quarter pipe. */
export function coping(q: QuarterPrim): Rail {
  const c = Math.cos(q.rot);
  const s = Math.sin(q.rot);
  const toWorld = (u: number, w: number): Vec2 => ({ x: q.x + u * c - w * s, z: q.z + u * s + w * c });
  return { a: toWorld(q.r, -q.hw), b: toWorld(q.r, q.hw), y: q.r, kind: "coping" };
}

export const QUARTER_PIECE: PieceKind<QuarterPiece> = {
  fields: ["x", "z", "rot", "r", "hw", "deck", "coping", "color"],
  placed: true,
  check(c, o, path, bounds) {
    c.num(o, "r", path, { min: 0.5, max: 20 });
    c.num(o, "deck", path, { min: 0, max: 2 * bounds });
    if (o.coping !== undefined && typeof o.coping !== "boolean") c.fail(`${path}.coping`, "expected true or false");
    checkColor(c, o, path);
  },
  compile(p, out) {
    const q: QuarterPrim = { kind: "quarter", x: p.x, z: p.z, rot: rad(p.rot), r: p.r, hw: p.hw, deck: p.deck };
    // What it looks like rides along only when given, so a map without it compiles exactly as before.
    out.prims.push(p.coping === false ? { ...withColor(q, p.color), bare: true } : withColor(q, p.color));
    if (p.coping !== false) out.rails.push(coping(q));
  },
};
